import { getCookie } from 'cookies-next';
import { isEmpty } from 'lodash-es';
import { useContext } from 'react';
import crypto from 'crypto';
import { requestAirbridgeTrackingLink } from '@apis/renewal/share';
import type { AirbridgeTrackingLinkResponse } from '@apis/renewal/share';
import { WckAuthCookie } from '@wconcept/wconcept-common/models/display';
import { ModalsContext } from '@wconcept/wconcept-ui-renewal/src/contexts/ModalsContext';
import {
  getLpProductListCols,
  parseLpProductListCols,
  setLpProductListCols,
} from '@wconcept/wconcept-common/utils/lpProductListCols';
import type { LpProductListCols } from '@wconcept/wconcept-common/utils/lpProductListCols';
import { WckReNewalPRT } from './appInterface';

const WCK_COMMON_RENEWAL_PRT = {
  app_scheme: 'wconceptkr',
  app_action_set_alarm: 'setAlarm',
};

const decodeCookie = (cookieKey: string, cookieName: string) => {
  let result;
  if (cookieKey) {
    try {
      // wckBridgeAuth 쿠키 key, iv default로 처리, cookieKey가 wckConnectionAuth 이면 wckConnectionAuth의 key, iv 사용
      let key =
        process.env.NEXT_PUBLIC_WCK_BRIDGE_AUTH_DATA_KEY ||
        (process.env.WCK_BRIDGE_AUTH_DATA_KEY as string);
      let iv =
        process.env.NEXT_PUBLIC_WCK_BRIDGE_AUTH_DATA_IV ||
        (process.env.WCK_BRIDGE_AUTH_DATA_IV as string);

      if (cookieName === process.env.NEXT_PUBLIC_WCK_AUTH_COOKIE_KEY) {
        key =
          process.env.NEXT_PUBLIC_WCK_AUTH_DATA_KEY ||
          (process.env.WCK_AUTH_DATA_KEY as string);
        iv =
          process.env.NEXT_PUBLIC_WCK_AUTH_DATA_IV ||
          (process.env.WCK_AUTH_DATA_IV as string);
      }

      // Base64 디코딩된 키와 IV를 사용
      const keyBuffer = Buffer.from(key, 'base64');
      const ivBuffer = Buffer.from(iv, 'base64');

      // 복호화
      const decipher = crypto.createDecipheriv(
        'aes-256-cbc',
        keyBuffer as unknown as crypto.CipherKey,
        ivBuffer as unknown as crypto.BinaryLike
      );
      let decrypted = decipher.update(cookieKey, 'base64', 'base64');
      decrypted += decipher.final('base64');

      // Base64 디코딩 및 UTF-8 문자열로 변환
      const authData = decodeURIComponent(
        Buffer.from(decrypted, 'base64').toString('utf8')
      );

      result = JSON.parse(authData) as unknown;
    } catch (e) {
      console.log(e);
    }
  }
  return result;
};

// WckAuthData 정보 조회.
const getCookieWckAuthData = () => {
  let wckAuthData: WckAuthCookie = {
    userNo: '0',
    userId: '',
    userName: '',
    gender: '',
    grade: '8',
    gradeName: '',
    birth: '',
    mobile: '',
    isCert: false,
    isPasswordChange: false,
    isEmployee: '',
    cooperationCompanyCode: '',
    isAuthLogin: false,
    lastLoginDate: '',
    joinDate: '',
    lastOrderDate: '',
    snsType: '',
    loginType: '',
    smsYN: '',
    emailYN: '',
    pushYN: '',
    adYN: '',
    liveAlarmYN: '',
    cookieCreateDate: '',
    locationAgreementYN: '',
    kakaotalkYN: '',
  };

  const bridgeCookieKey = process.env
    .NEXT_PUBLIC_WCK_BRIDGE_AUTH_COOKIE_KEY as string;
  const cookieKey = process.env.NEXT_PUBLIC_WCK_AUTH_COOKIE_KEY as string;

  let wckAuthCookie = '';
  let cookieName = bridgeCookieKey;

  wckAuthCookie = getCookie(bridgeCookieKey) as string;
  if (isEmpty(wckAuthCookie)) {
    cookieName = cookieKey;
    wckAuthCookie = getCookie(cookieKey) as string;
  }

  const auth = decodeCookie(wckAuthCookie, cookieName) as WckAuthCookie;
  if (auth) {
    wckAuthData = auth;
    if (isEmpty(auth.userNo)) {
      if (auth.grade === '0') {
        auth.grade = '6';
      }
    } else {
      auth.grade = '8';
    }
  }

  if (!isEmpty(wckAuthData.userNo) && isEmpty(wckAuthData.kakaotalkYN)) {
    wckAuthData.kakaotalkYN = 'N';
  }
  return wckAuthData;
};

const setCookie = (name: string, value: string, exp: number) => {
  const date = new Date();
  date.setTime(date.getTime() + exp * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};domain=.wconcept.co.kr;expires=${date.toUTCString()};path=/`;
};

// 최근 검색한 검색어 저장
export const setLastKwd = async (keyword: string) => {
  if (isEmpty(keyword)) {
    return;
  }

  const date = new Date();
  // 24시간 저장
  date.setHours(date.getHours() + 24);

  setCookie('LastKwd', keyword, date.getTime());
};

const useWckAppFunction = () => {
  const { closeModal } = useContext(ModalsContext); // 모달 Context

  const setLastKeyword = (keyword: string) => {
    setLastKwd(keyword);
  };

  const getUserInfo = () => {
    const { isAuthLogin } = getCookieWckAuthData();
    return `${getCookie('custno') as string}|${
      isAuthLogin ? 'true' : 'false'
    }|${getCookie('wckGenderValue') as string}|${
      getCookie('BasketCnt') as string
    }`;
  };

  const changeGender = (value: 'Women' | 'Men') => {
    setCookie('wckGenderValue', value, 10000);
  };

  const getBasketCnt = () => getCookie('BasketCnt') as string;

  const getWckLpProductListCols = () => getLpProductListCols();

  const setWckLpProductListCols = (cols: LpProductListCols | string) => {
    setLpProductListCols(parseLpProductListCols(cols));
  };

  const actionHistoryBack = () => {
    if (WckReNewalPRT().isWckNewApp()) {
      document.location.href = 'wconceptkr://webViewBack';
    } else {
      window.history.back();
    }
  };

  const nativeHeaderOn = () => {
    try {
      if (WckReNewalPRT().isWckNewApp()) {
        document.location.href = 'wconceptkr://headerDimOn';
      }
    } catch (e) {
      console.error(e);
    }
  };

  const nativeHeaderOff = () => {
    try {
      if (WckReNewalPRT().isWckNewApp()) {
        document.location.href = 'wconceptkr://headerDimOff';
      }
    } catch (e) {
      console.error(e);
    }
  };

  const clearFilterModal = () => {};

  const actionApp = (action: string, param: { [key: string]: any }) => {
    let queryString = '';
    if (param) {
      queryString = `?${new URLSearchParams(param).toString()}`;
    }

    document.location.href = `${WCK_COMMON_RENEWAL_PRT.app_scheme}://${action}${queryString}`;
  };

  const setAlarm = (
    isSet: boolean,
    callback: string,
    { custno, custid }: { custno: string; custid: string }
  ) => {
    if (WckReNewalPRT().isApp()) {
      actionApp(WCK_COMMON_RENEWAL_PRT.app_action_set_alarm, {
        isSet,
        custno,
        custid,
        callback,
      });
    }
  };

  const setAlarmComplete = (
    code: number,
    message: string,
    successMessage: string
  ) => {
    code === 200 ? alert(successMessage) : message && alert(message);
  };

  const SGAppPushResultSend = (isSet: string) => {
    if (document.getElementById('SgAppTrackFrmD') === null) {
      const iframe = document.createElement('iframe');
      iframe.id = 'SgAppTrackFrmD';
      iframe.name = 'SgAppTrackFrmD';
      iframe.src = '';
      iframe.width = '0';
      iframe.height = '0';
      document.body.insertBefore(iframe, document.body.firstChild);
    }

    let sgJson = '';
    sgJson += '{';
    sgJson += `"CustNo": "${getCookie('custno')}",`;
    sgJson += `"AgreeYN": "${isSet}",`;
    sgJson += `"PushToken": "${getCookie('PUSH_TOKEN')}",`;
    sgJson += `"DeviceUuid": "${getCookie('DEVICE_UUID')}"`;
    sgJson += '}';

    const sgAppTrackFrmD = document.getElementById('SgAppTrackFrmD');
    if (sgAppTrackFrmD) {
      sgAppTrackFrmD.setAttribute(
        'src',
        `wconceptkr://SgAppPush?JsonString=${encodeURIComponent(sgJson)}`
      );
    }
  };

  const enableAlarmComplete = (code: number, message: string) => {
    const now = new Date();
    SGAppPushResultSend('Y');
    setAlarmComplete(
      code,
      message,
      `PUSH 수신동의 처리가 완료되었습니다.(${now.getFullYear()}. ${
        now.getMonth() + 1
      }. ${now.getDate()})`
    );
  };

  const disableAlarmComplete = (code: number, message: string) => {
    const now = new Date();
    SGAppPushResultSend('N');
    setAlarmComplete(
      code,
      message,
      `PUSH 수신거부 처리가 완료되었습니다.(${now.getFullYear()}. ${
        now.getMonth() + 1
      }. ${now.getDate()})`
    );
  };

  const verifiedYn = () => {
    const user = getCookieWckAuthData();
    return user?.mobile !== '' ? 'Y' : 'N';
  };

  const getAirbridgeShareUrl = async (): Promise<
    AirbridgeTrackingLinkResponse | undefined
  > => {
    const context = window.WCK_AIRBRIDGE_SHARE_CONTEXT;
    const shareUrl = context?.shareUrl || window.location.href;
    const type = context?.type;

    if (!type) {
      return { trackingUrl: shareUrl };
    }

    try {
      const shareInfo = await requestAirbridgeTrackingLink({
        type,
        shareUrl,
        imageUrl: context?.imageUrl,
        profileName: context?.profileName,
        description: context?.description,
      });

      return shareInfo || { trackingUrl: window.location.href };
    } catch (error) {
      console.error('Airbridge 공유 URL 생성에 실패했습니다.', error);
      return { trackingUrl: window.location.href };
    }
  };

  return {
    setLastKeyword,
    getUserInfo,
    changeGender,
    getBasketCnt,
    getWckLpProductListCols,
    setWckLpProductListCols,
    actionHistoryBack,
    nativeHeaderOn,
    nativeHeaderOff,
    clearFilterModal,
    actionApp,
    getAirbridgeShareUrl,
    setAlarm,
    enableAlarmComplete,
    disableAlarmComplete,
    verifiedYn,
  };
};

export default useWckAppFunction;
